// src/cockpit4/workspaces/CRMWorkspace.tsx
// ============================================
// CRM Workspace - 复用 CRMDashboard 组件 + 客户档案面板
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Folder,
  FolderOpen,
  FileText,
  Download,
  Loader2,
  RefreshCw,
  Filter,
  CalendarClock,
  Radar,
  LayoutDashboard,
  Sparkles,
  Calculator,
  HeartHandshake,
  FileSignature,
  // P2 标签图标
  AlertTriangle,
  Repeat,
  MessageCircle,
  ChevronDown,
  // V1.5 P1-5/P1-6：Offer 设计 + AI 成交
  Package,
  Target,
} from "lucide-react";
import CRMDashboard from "../../components/Dashboard/CRMDashboard";
import CustomerArchivePanel from "../components/CustomerArchivePanel";
// P0-功能1：智能销售漏斗
import SalesFunnel from "../components/SalesFunnel";
// P0-功能2：智能跟进助手
import FollowUpAssistant from "../components/FollowUpAssistant";
// P0-功能3：客户 360° 画像
import Customer360 from "../components/Customer360";
// V1.5 P1-6：AI Deal Maker（决策式成交卡：痛点→概率→推荐产品→切入点→今日一步）
import DealMakerPanel from "../components/DealMakerPanel";
// V1.5 P1-5：Offer 产品设计（需求→产品包→报价→毛利，复用报价谈判）
import OfferDesignPanel from "../components/OfferDesignPanel";
// P1-功能1：智能线索挖掘
import LeadMiner from "../components/LeadMiner";
// P1-功能2：智能报价与谈判助手
import QuoteNegotiator from "../components/QuoteNegotiator";
// P1-功能3：客户成功管理
import CustomerSuccess from "../components/CustomerSuccess";
// P1-功能4：合同与订单管理
import ContractManager from "../components/ContractManager";
// P2-功能8：流失预警与智能挽回
import ChurnWarningPanel from "../components/ChurnWarningPanel";
// P2-功能9：智能推荐与交叉销售
import CrossSellRecommender from "../components/CrossSellRecommender";
// P2-功能10：客户社群运营
import CustomerCommunity from "../components/CustomerCommunity";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useCockpit4Store, type CrmRiskItem } from "../store";
// Phase3 P2-4：老板只读模式
import { useReadonlyMode } from "../hooks/useReadonlyMode";
// Phase 3 P0-3：页面顶部业务说明 + 跨模块跳转
import WorkspaceBizBar from "../components/WorkspaceBizBar";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { CustomerProfile } from "../../runtime/CRMRuntime/types";

export default function CRMWorkspace() {
  const dashboard = useDashboardData();
  const setCrmRisks = useCockpit4Store((s) => s.setCrmRisks);
  // Phase3 P2-4：老板只读模式（禁用导入等写操作）
  const { readonly, guard } = useReadonlyMode();

  // ============ 酒店通信录（Excel 档案）状态 ============
  // 酒店客户档案根目录（Vault 相对路径）
  const HOTEL_CONTACTS_ROOT = "XEAOS/CRM/酒店客户档案";
  const [hotelContacts, setHotelContacts] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  // 当前浏览的子目录（支持展开地级市酒店汇总、全国酒店客户档案等子目录）
  const [contactsDir, setContactsDir] = useState<string>(HOTEL_CONTACTS_ROOT);
  // 导入状态（Excel → CRM 客户档案）
  const [importingContacts, setImportingContacts] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);

  // 标签页切换：P0（数据看板 / 销售漏斗 / 跟进助手 / 客户360）+ P1（线索挖掘 / 报价谈判 / 客户成功 / 合同管理）
  const [activeSection, setActiveSection] = useState<
    | "dashboard"
    | "funnel"
    | "followup"
    | "customer360"
    | "dealMaker"
    | "leadMiner"
    | "quoteNegotiator"
    | "offerDesign"
    | "customerSuccess"
    | "contractManager"
    | "churnWarning"
    | "crossSell"
    | "community"
  >("dashboard");
  // v6 收敛：低频功能收进"更多▾"下拉
  const [showMoreSections, setShowMoreSections] = useState(false);

  // 导入酒店通信录 Excel → CRM 客户档案（调用 server.py import-hotel-xlsx 端点）
  const handleImportContacts = async () => {
    setImportingContacts(true);
    setImportResult(null);
    try {
      const resp = await fetch("/vault-api?action=import-hotel-xlsx");
      const data = await resp.json();
      if (data?.success) {
        setImportResult({
          success: true,
          message: `✅ 已导入 ${data.imported} 家酒店客户 → ${data.outDir}`,
        });
      } else {
        setImportResult({ success: false, message: `⚠️ 导入失败：${data?.error || "未知错误"}` });
      }
    } catch (err: any) {
      setImportResult({ success: false, message: `⚠️ 导入异常：${err?.message || String(err)}` });
    } finally {
      setImportingContacts(false);
    }
  };

  /**
   * 加载酒店通信录文件列表
   * 调用 server.mjs 的 list-files 端点（返回所有文件类型，含 xlsx，区别于只返回 .md 的 list）
   * 返回格式：{ success: true, files: [{ path, name, size, mtime, isDirectory }] }
   */
  const loadHotelContacts = useCallback(async (dir?: string) => {
    const targetDir = dir || contactsDir;
    setLoadingContacts(true);
    try {
      const resp = await fetch(
        "/vault-api?action=list-files&dir=" + encodeURIComponent(targetDir)
      );
      const data = await resp.json();
      if (data.success) {
        setHotelContacts(data.files || []);
        if (dir) setContactsDir(dir);
      } else {
        setHotelContacts([]);
      }
    } catch (e) {
      console.error("加载酒店通信录失败:", e);
      setHotelContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [contactsDir]);

  // 组件挂载时自动加载酒店通信录
  useEffect(() => {
    loadHotelContacts(HOTEL_CONTACTS_ROOT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 下载酒店通信录文件（通过 vault-api 的 download 端点返回二进制流） */
  const downloadContactFile = (filePath: string) => {
    window.open(
      `/vault-api?action=download&path=${encodeURIComponent(filePath)}`,
      "_blank"
    );
  };

  /** 格式化文件大小（B/KB/MB） */
  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // 加载客户数据后，提取风险客户（30天未联系 + 快成交未跟进）联动到 CEO 风险监控面板
  useEffect(() => {
    let cancelled = false;
    const extractRisks = async () => {
      // 轮询等待 RuntimeManager 就绪
      for (let i = 0; i < 30; i++) {
        if (cancelled) return;
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (cancelled || !RuntimeManager.isInitialized()) return;

      try {
        const resp = await RuntimeManager.query(RuntimeName.CRM, {
          type: "list",
        });
        if (cancelled || !resp?.success || !resp.data) return;
        const inner = resp.data as any;
        const list: CustomerProfile[] = inner?.data || inner || [];
        if (!Array.isArray(list)) return;

        const now = Date.now();
        const DAY_MS = 24 * 60 * 60 * 1000;
        const risks: CrmRiskItem[] = [];

        for (const c of list) {
          // 计算距上次联系天数
          const last = new Date(c.followup?.lastContact || "");
          const daysSinceContact = isNaN(last.getTime())
            ? 0
            : Math.floor((now - last.getTime()) / DAY_MS);

          // 30天未联系 → stale 风险
          if (daysSinceContact >= 30) {
            risks.push({
              customerName: c.company,
              type: "stale",
              daysSinceContact,
              stage: c.followup?.status || "",
              industry: c.industry || "",
            });
          }

          // 快成交未跟进（谈判中）→ closing 风险
          if (c.followup?.status === "negotiating") {
            risks.push({
              customerName: c.company,
              type: "closing",
              daysSinceContact,
              stage: c.followup.status,
              industry: c.industry || "",
            });
          }
        }

        if (!cancelled) {
          setCrmRisks(risks);
        }
      } catch {
        // 降级：不更新风险列表
      }
    };
    extractRisks();
    return () => {
      cancelled = true;
    };
  }, [setCrmRisks]);

  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Users size={14} className="text-blue-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                客户中心
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                客户流水线 · 销售管道 · 档案备案
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            Lead → Opportunity → Contract → Payment → Renew
          </span>
        </div>

        {/* Phase3 P0-3 业务说明 + 跨模块跳转 */}
        <WorkspaceBizBar
          desc="客户中心：管理全渠道线索；线索来自【GEO+DSO 挖掘】和【营销中心内容引流】"
          accent="#3B82F6"
          jumps={[
            { label: "GEO+DSO 挖掘", to: "geodso" },
            { label: "内容引流", to: "marketing" },
          ]}
        />

        {/* v6 收敛：核心 6 项平铺（含客户社群） + 低频 5 项收进"更多▾" */}
        <div className="relative z-30 flex items-center gap-1 px-4 py-2 border-b border-white/[0.06] bg-white/[0.01] overflow-x-auto">
          {/* 核心标签组（客户链路 + 保留的社群业务） */}
          <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider px-1 select-none">
            核心
          </span>
          {(
            [
              { id: "dashboard", label: "数据看板", icon: LayoutDashboard },
              { id: "funnel", label: "销售漏斗", icon: Filter },
              { id: "followup", label: "跟进助手", icon: CalendarClock },
              { id: "customer360", label: "客户360", icon: Radar },
              // V1.5 P1-6：AI 成交（决策式成交卡）
              { id: "dealMaker", label: "AI成交", icon: Target },
              { id: "customerSuccess", label: "客户成功", icon: HeartHandshake },
              { id: "community", label: "客户社群", icon: MessageCircle },
            ] as const
          ).map((tab) => {
            const TIcon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                  isActive
                    ? "bg-blue-500/15 text-blue-300 border border-blue-500/30"
                    : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <TIcon size={11} />
                {tab.label}
              </button>
            );
          })}

          {/* 更多▾ 下拉 */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowMoreSections(!showMoreSections)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                showMoreSections
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span>更多</span>
              <ChevronDown size={10} className={showMoreSections ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {showMoreSections && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMoreSections(false)} />
                <div className="absolute top-full left-0 mt-1 z-40 min-w-[160px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl">
                  {(
                    [
                      { id: "leadMiner", label: "线索挖掘", icon: Sparkles },
                      { id: "quoteNegotiator", label: "报价谈判", icon: Calculator },
                      // V1.5 P1-5：Offer 产品设计（需求→产品包→报价→毛利）
                      { id: "offerDesign", label: "Offer设计", icon: Package },
                      { id: "contractManager", label: "合同管理", icon: FileSignature },
                      { id: "churnWarning", label: "流失预警", icon: AlertTriangle },
                      { id: "crossSell", label: "交叉销售", icon: Repeat },
                    ] as const
                  ).map((tab) => {
                    const TIcon = tab.icon;
                    const isActive = activeSection === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveSection(tab.id);
                          setShowMoreSections(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                          isActive
                            ? "text-blue-300 bg-blue-500/10"
                            : "text-gray-300 hover:bg-white/[0.05]"
                        }`}
                      >
                        <TIcon size={11} className="text-blue-400" />
                        <span className="flex-1 text-left">{tab.label}</span>
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {activeSection === "dashboard" && (
            <>
              <CRMDashboard data={dashboard.crm} />
              {/* 客户档案与跟进备案（分类筛选 + 详情查看） */}
              <CustomerArchivePanel />

          {/* ============ 酒店通信录（Excel 档案，支持子目录展开） ============ */}
          <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden">
            {/* 头部 */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.04]">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <FolderOpen size={12} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-[12px] font-medium text-white">酒店通信录</h3>
                  <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                    Hotel Contacts · Excel 档案 · {hotelContacts.length} 项
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => guard(handleImportContacts)}
                  disabled={importingContacts || readonly}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title={readonly ? "老板只读模式：不可导入数据" : "将 Excel 酒店通信录解析为 CRM 客户档案（约 20 秒，导入到 XEAOS/CRM/酒店客户档案/导入/）"}
                >
                  {importingContacts ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                  {importingContacts ? "导入中..." : "导入到 CRM"}
                </button>
                <button
                  onClick={() => loadHotelContacts()}
                  disabled={loadingContacts}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-40"
                >
                  <RefreshCw size={10} className={loadingContacts ? "animate-spin" : ""} />
                  刷新
                </button>
              </div>
            </div>

            {/* 导入结果提示 */}
            {importResult && (
              <div className={`px-4 py-1.5 border-b border-white/[0.03] text-[10px] font-mono ${
                importResult.success ? "text-emerald-400 bg-emerald-500/[0.04]" : "text-red-400 bg-red-500/[0.04]"
              }`}>
                {importResult.message}
              </div>
            )}

            {/* 面包屑导航：支持返回上级目录 */}
            <div className="px-4 py-1.5 border-b border-white/[0.03] flex items-center gap-1 text-[10px] font-mono text-gray-500">
              <button
                onClick={() => loadHotelContacts(HOTEL_CONTACTS_ROOT)}
                className="hover:text-amber-400 transition-colors"
              >
                酒店客户档案
              </button>
              {contactsDir !== HOTEL_CONTACTS_ROOT && (
                <span className="text-gray-600">
                  / {contactsDir.replace(`${HOTEL_CONTACTS_ROOT}/`, "")}
                </span>
              )}
              {contactsDir !== HOTEL_CONTACTS_ROOT && (
                <button
                  onClick={() => {
                    // 返回上一级目录
                    const parent = contactsDir.split("/").slice(0, -1).join("/");
                    loadHotelContacts(parent || HOTEL_CONTACTS_ROOT);
                  }}
                  className="ml-auto px-1.5 py-0.5 rounded text-[9px] text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
                >
                  ↑ 返回上级
                </button>
              )}
            </div>

            {/* 文件列表 */}
            <div className="p-3 max-h-[420px] overflow-y-auto">
              {loadingContacts ? (
                <div className="flex items-center justify-center py-6 text-gray-500">
                  <Loader2 size={14} className="animate-spin mr-2" />
                  <span className="text-[11px]">加载酒店通信录中...</span>
                </div>
              ) : hotelContacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-gray-600">
                  <FileText size={20} className="mb-2 opacity-50" />
                  <span className="text-[11px]">暂无酒店通信录文件</span>
                  <span className="text-[9px] font-mono text-gray-700 mt-1">
                    期望路径：{HOTEL_CONTACTS_ROOT}/
                  </span>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* 子目录排在前面，方便展开地级市酒店汇总、全国酒店客户档案等 */}
                  {[...hotelContacts]
                    .sort((a, b) => Number(b.isDirectory) - Number(a.isDirectory))
                    .map((file, i) => (
                      <div
                        key={`${file.path}-${i}`}
                        className="flex items-center justify-between py-1.5 px-2 rounded border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.02] transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {file.isDirectory ? (
                            <Folder size={12} className="text-amber-400 flex-shrink-0" />
                          ) : (
                            <FileText size={12} className="text-cyan-400 flex-shrink-0" />
                          )}
                          {file.isDirectory ? (
                            <button
                              onClick={() => loadHotelContacts(file.path)}
                              className="text-[11px] text-amber-300 hover:text-amber-200 transition-colors truncate text-left"
                              title="点击进入子目录"
                            >
                              {file.name}/
                            </button>
                          ) : (
                            <span className="text-[11px] text-gray-300 truncate" title={file.name}>
                              {file.name}
                            </span>
                          )}
                        </div>
                        {!file.isDirectory && (
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-[9px] font-mono text-gray-600">
                              {formatFileSize(file.size)}
                            </span>
                            {file.mtime && (
                              <span className="text-[9px] font-mono text-gray-600">
                                {file.mtime.slice(0, 10)}
                              </span>
                            )}
                            <button
                              onClick={() => downloadContactFile(file.path)}
                              className="flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded transition-colors"
                              title="下载文件"
                            >
                              <Download size={9} />
                              下载
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
            </>
          )}
          {activeSection === "funnel" && <SalesFunnel />}
          {activeSection === "followup" && <FollowUpAssistant />}
          {activeSection === "customer360" && <Customer360 />}
          {/* V1.5 P1-6：AI 成交（决策式成交卡） */}
          {activeSection === "dealMaker" && <DealMakerPanel />}
          {/* V1.5 P1-5：Offer 产品设计 */}
          {activeSection === "offerDesign" && <OfferDesignPanel />}
          {/* P1 组件 */}
          {activeSection === "leadMiner" && <LeadMiner />}
          {activeSection === "quoteNegotiator" && <QuoteNegotiator />}
          {activeSection === "customerSuccess" && <CustomerSuccess />}
          {activeSection === "contractManager" && <ContractManager />}

          {/* P2 组件 */}
          {activeSection === "churnWarning" && <ChurnWarningPanel />}
          {activeSection === "crossSell" && <CrossSellRecommender />}
          {activeSection === "community" && <CustomerCommunity />}
        </div>
      </motion.div>
    </div>
  );
}
